import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const Z_PORTAL = 2147483647;

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const pad2 = (n) => String(n).padStart(2, '0');

export const toDateInputValue = (date) => {
    if (!date || Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

export const parseDateInputValue = (value) => {
    if (!value) return null;
    const parts = String(value).split('-').map((p) => Number(p));
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return null;
    }
    return date;
};

const formatDisplayDate = (value) => {
    const parsed = parseDateInputValue(value);
    if (!parsed) return '';
    return parsed.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const buildMonthGrid = (viewDate) => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < startOffset; i += 1) {
        const day = new Date(year, month, -startOffset + i + 1);
        cells.push({ date: day, inMonth: false });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
        cells.push({ date: new Date(year, month, day), inMonth: true });
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
        const last = cells[cells.length - 1].date;
        const next = new Date(
            last.getFullYear(),
            last.getMonth(),
            last.getDate() + 1,
        );
        cells.push({ date: next, inMonth: false });
    }
    return cells;
};

const ThemedDateInput = ({
    value = '',
    onChange,
    name,
    id,
    disabled = false,
    placeholder = 'Select date',
    className = '',
    hasError = false,
    minDate,
    disablePast = true,
}) => {
    const [open, setOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState(null);
    const [viewMonth, setViewMonth] = useState(() => {
        const parsed = parseDateInputValue(value);
        return parsed ? new Date(parsed.getFullYear(), parsed.getMonth(), 1) : new Date();
    });
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const listId = useId();

    const minDay = useMemo(() => {
        if (minDate) return startOfDay(minDate);
        if (disablePast) return startOfDay(new Date());
        return null;
    }, [minDate, disablePast]);

    const selectedDate = parseDateInputValue(value);
    const displayText = formatDisplayDate(value) || placeholder;
    const today = startOfDay(new Date());

    const cells = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);

    const updatePanelPosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const gap = 6;
        const viewportPadding = 8;
        const panelWidth = Math.max(rect.width, 300);
        let left = rect.left;
        left = Math.min(
            Math.max(viewportPadding, left),
            window.innerWidth - panelWidth - viewportPadding,
        );

        const panelHeight = 360;
        let top = rect.bottom + gap;
        if (top + panelHeight > window.innerHeight - viewportPadding) {
            const above = rect.top - gap - panelHeight;
            if (above >= viewportPadding) top = above;
        }

        setPanelStyle({
            position: 'fixed',
            top,
            left,
            width: panelWidth,
            zIndex: Z_PORTAL,
        });
    };

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePanelPosition();
        const onReposition = () => updatePanelPosition();
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            const target = event.target;
            if (
                rootRef.current?.contains(target) ||
                panelRef.current?.contains(target)
            ) {
                return;
            }
            setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    useEffect(() => {
        const parsed = parseDateInputValue(value);
        if (parsed) {
            setViewMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
        }
    }, [value]);

    const emitChange = (nextValue) => {
        onChange?.({
            target: {
                name: name || undefined,
                value: nextValue,
            },
        });
    };

    const pickDate = (date) => {
        if (minDay && startOfDay(date) < minDay) return;
        emitChange(toDateInputValue(date));
        setOpen(false);
    };

    const panel =
        open && panelStyle ? (
            <div
                ref={panelRef}
                id={listId}
                role="dialog"
                aria-label="Choose date"
                style={panelStyle}
                className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl shadow-zinc-900/10 ring-1 ring-zinc-900/5 dark:border-zinc-700 dark:bg-zinc-900 dark:shadow-black/40 dark:ring-white/10"
            >
                <div className="flex items-center justify-between gap-2 border-b border-zinc-200 px-3 py-2.5 dark:border-zinc-700">
                    <button
                        type="button"
                        onClick={() =>
                            setViewMonth(
                                (current) =>
                                    new Date(
                                        current.getFullYear(),
                                        current.getMonth() - 1,
                                        1,
                                    ),
                            )
                        }
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                        aria-label="Previous month"
                    >
                        <i className="fa-solid fa-chevron-left text-xs" />
                    </button>
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                        {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                    </p>
                    <button
                        type="button"
                        onClick={() =>
                            setViewMonth(
                                (current) =>
                                    new Date(
                                        current.getFullYear(),
                                        current.getMonth() + 1,
                                        1,
                                    ),
                            )
                        }
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                        aria-label="Next month"
                    >
                        <i className="fa-solid fa-chevron-right text-xs" />
                    </button>
                </div>

                <div className="grid grid-cols-7 gap-0.5 px-3 pt-2">
                    {WEEKDAYS.map((label) => (
                        <div
                            key={label}
                            className="py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500"
                        >
                            {label}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 gap-0.5 px-3 pb-2">
                    {cells.map(({ date, inMonth }) => {
                        const isSelected = isSameDay(date, selectedDate);
                        const isToday = isSameDay(date, today);
                        const isDisabled =
                            minDay && startOfDay(date) < minDay;
                        return (
                            <button
                                key={toDateInputValue(date)}
                                type="button"
                                disabled={isDisabled}
                                onClick={() => pickDate(date)}
                                className={`flex h-9 w-full items-center justify-center rounded-lg text-sm transition-colors ${
                                    isDisabled
                                        ? 'cursor-not-allowed text-zinc-300 dark:text-zinc-600'
                                        : inMonth
                                          ? 'text-zinc-800 hover:bg-teal-50 dark:text-zinc-200 dark:hover:bg-teal-950/40'
                                          : 'text-zinc-400 hover:bg-zinc-50 dark:text-zinc-500 dark:hover:bg-zinc-800/50'
                                } ${
                                    isSelected
                                        ? '!bg-teal-600 !font-semibold !text-white hover:!bg-teal-700 dark:!bg-teal-600'
                                        : ''
                                } ${
                                    isToday && !isSelected
                                        ? 'ring-1 ring-teal-500/50 ring-inset'
                                        : ''
                                }`}
                            >
                                {date.getDate()}
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between border-t border-zinc-200 px-3 py-2 dark:border-zinc-700">
                    <button
                        type="button"
                        onClick={() => {
                            emitChange('');
                            setOpen(false);
                        }}
                        className="text-xs font-semibold text-zinc-500 transition-colors hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        onClick={() => pickDate(today)}
                        className="text-xs font-semibold text-teal-700 transition-colors hover:text-teal-900 dark:text-teal-400 dark:hover:text-teal-300"
                    >
                        Today
                    </button>
                </div>
            </div>
        ) : null;

    return (
        <div ref={rootRef} className={`relative ${className}`}>
            <button
                ref={triggerRef}
                type="button"
                id={id}
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => {
                    if (!disabled) setOpen((prev) => !prev);
                }}
                className={`cpg-input flex w-full cursor-pointer items-center justify-between gap-2 text-left ${
                    disabled ? 'cursor-not-allowed opacity-60' : ''
                } ${hasError ? 'border-rose-400/70 focus:border-rose-500 focus:ring-rose-500/30 dark:border-rose-500/50' : ''} ${
                    !value
                        ? 'text-zinc-400 dark:text-zinc-500'
                        : 'text-zinc-900 dark:text-zinc-100'
                }`}
            >
                <span className="truncate">{displayText}</span>
                <i
                    className="fa-regular fa-calendar shrink-0 text-sm text-teal-700 dark:text-teal-400"
                    aria-hidden
                />
            </button>
            {panel ? createPortal(panel, document.body) : null}
        </div>
    );
};

export default ThemedDateInput;
